import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Save, Info, Building, Check, AlertCircle, MapPin, ExternalLink } from 'lucide-react';
import FormPageLayout from '../common/FormPageLayout';
import { LoadingSpinner } from '../common';
import Banner from '../common/Banner';
import { useToast } from '../common/Toast';
import {
  createAgency,
  updateAgency,
  getAgencyByTenantId,
  getEvvOnboarding,
  updateEvvOnboarding,
  getAgencyFieldErrors,
  getAgencyProfileMissing,
} from '../../services/admin/agency.service';
import {
  Agency,
  AgencyData,
  AGENCY_TIMEZONES,
  DEFAULT_AGENCY_TIMEZONE,
  EVVOnboardingStepStatus,
  EvvOnboardingDto,
  EvvOnboardingStepDto,
} from '../../dtos/admin/agency.dto';
import { useAuth } from '../../context/authcontext';
import { useCanPerformAll } from '../../hooks/useCanPerform';
import { SETUP_STATUS_UPDATED_EVENT } from '../../constants/navigationEvents';
import { disciplineCodes, disciplineLabel, disciplineLongLabel } from '../../constants/disciplines';
import { clearServicesOfferedCache } from '../../hooks/useServicesOffered';

// ─── Validation (mirrors the backend agency profile rules, Phase 5 D6) ───────

// NUCC taxonomy: 3 digits, 6 letters/digits, then a digit or X (e.g. 251E00000X, home health)
const TAXONOMY_PATTERN = /^\d{3}[0-9A-Z]{6}[0-9X]$/;
const ZIP5_PATTERN = /^\d{5}$/;
const ZIP4_PATTERN = /^\d{4}$/;
const BENEFIT_CODE_MAX = 20;
/** Fields the PA forms need; saving works without them but PA generation returns 422 */
export const PA_FORMS_NOTE = 'Required for PA forms';
const TIMEZONE_VALUES: readonly string[] = AGENCY_TIMEZONES.map((tz) => tz.value);

/** NPI check digit: Luhn over "80840" + the first 9 digits (CMS NPI standard). */
export function isValidNpi(npi: string): boolean {
  if (!/^\d{10}$/.test(npi)) return false;
  const digits = `80840${npi.slice(0, 9)}`;
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === Number(npi[9]);
}

/** Phase 22 (EVV G2): TMHP API number: 1–15 letters or digits (mirrors the backend). */
const EVV_API_PATTERN = /^[A-Za-z0-9]{1,15}$/;
export const EVV_LINKED_ID_NOTE = 'The NPI or API linked to your Texas EVV Service Provider ID in HHAeXchange. '
  + "TMHP denies EVV claims (EVV04) when the claim's NPI or API does not match. Recommended.";
export const EVV_LINKED_ID_HINT = 'Recommended: add the NPI or API linked to your EVV account';

/** EVV-linked NPI / API checks; both may be blank (clearing is allowed). */
export function validateEvvLinkedIds(npi: string, api: string): Partial<Record<'evvProviderNpi' | 'evvProviderApi', string>> {
  const errors: Partial<Record<'evvProviderNpi' | 'evvProviderApi', string>> = {};
  const n = npi.trim();
  const a = api.trim();
  if (n && !/^\d{10}$/.test(n)) errors.evvProviderNpi = 'NPI must be a 10-digit number';
  else if (n && !isValidNpi(n)) errors.evvProviderNpi = 'NPI check digit is not valid. Check the number and try again';
  if (a && !EVV_API_PATTERN.test(a)) errors.evvProviderApi = 'API must be 1–15 letters or digits';
  return errors;
}

interface AgencyFormState {
  legalName: string;
  dba: string;
  npi: string;
  taxId: string;
  taxonomyCode: string;
  benefitCode: string;
  timezone: string;
  medicaidNumber: string;
  medicareNumber: string;
  stateLicenseNumber: string;
  officePhone: string;
  fax: string;
  billingEmail: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  zipPlus4: string;
  // EVV setup fields (saved through the evv-onboarding route)
  evvProviderId: string;
  evvProviderNpi: string;
  evvProviderApi: string;
  evvAdminName: string;
  evvAdminEmail: string;
}

type EvvField = 'evvProviderId' | 'evvProviderNpi' | 'evvProviderApi' | 'evvAdminName' | 'evvAdminEmail';
type ProfileField = Exclude<keyof AgencyFormState, EvvField>;
type EvvSaved = Pick<AgencyFormState, EvvField>;
const EMPTY_EVV: EvvSaved = { evvProviderId: '', evvProviderNpi: '', evvProviderApi: '', evvAdminName: '', evvAdminEmail: '' };
type FormErrors = Partial<Record<keyof AgencyFormState, string>>;

const EMPTY_FORM: AgencyFormState = {
  legalName: '',
  dba: '',
  npi: '',
  taxId: '',
  taxonomyCode: '',
  benefitCode: '',
  timezone: DEFAULT_AGENCY_TIMEZONE,
  medicaidNumber: '',
  medicareNumber: '',
  stateLicenseNumber: '',
  officePhone: '',
  fax: '',
  billingEmail: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  zipPlus4: '',
  ...EMPTY_EVV,
};

/** Backend field names (possibly dotted, e.g. "address.zipCode") → form field */
const SERVER_FIELD_MAP: Record<string, ProfileField | 'evvProviderNpi' | 'evvProviderApi'> = {
  evvProviderNpi: 'evvProviderNpi',
  evvProviderApi: 'evvProviderApi',
  legalName: 'legalName',
  dba: 'dba',
  npi: 'npi',
  taxId: 'taxId',
  taxonomyCode: 'taxonomyCode',
  taxonomy: 'taxonomyCode',
  benefitCode: 'benefitCode',
  timezone: 'timezone',
  medicaidNumber: 'medicaidNumber',
  medicareNumber: 'medicareNumber',
  stateLicenseNumber: 'stateLicenseNumber',
  officePhone: 'officePhone',
  fax: 'fax',
  billingEmail: 'billingEmail',
  street: 'street',
  city: 'city',
  state: 'state',
  zipCode: 'zipCode',
  zip: 'zipCode',
  zipPlus4: 'zipPlus4',
};

/** Split a legacy "12345-6789" ZIP into its parts (the backend also does this on read). */
function splitZip(zipCode: string | undefined | null, zipPlus4: string | undefined | null): { zip: string; plus4: string } {
  const raw = (zipCode || '').trim();
  const match = /^(\d{5})-?(\d{4})$/.exec(raw);
  if (match) return { zip: match[1], plus4: zipPlus4 || match[2] };
  return { zip: raw, plus4: zipPlus4 || '' };
}

function agencyToForm(agency: Agency): AgencyFormState {
  const { zip, plus4 } = splitZip(agency.address?.zipCode, agency.address?.zipPlus4 ?? agency.zipPlus4);
  const timezone = agency.timezone && TIMEZONE_VALUES.includes(agency.timezone) ? agency.timezone : DEFAULT_AGENCY_TIMEZONE;
  return {
    legalName: agency.legalName || '',
    dba: agency.dba || '',
    npi: agency.npi || '',
    taxId: agency.taxId || '',
    taxonomyCode: agency.taxonomyCode || '',
    benefitCode: agency.benefitCode || '',
    timezone,
    medicaidNumber: agency.medicaidNumber || '',
    medicareNumber: agency.medicareNumber || '',
    stateLicenseNumber: agency.stateLicenseNumber || '',
    officePhone: agency.contactInfo?.officePhone || '',
    fax: agency.contactInfo?.fax || '',
    billingEmail: agency.contactInfo?.billingEmail || '',
    street: agency.address?.street || '',
    city: agency.address?.city || '',
    state: agency.address?.state || '',
    zipCode: zip,
    zipPlus4: plus4,
    evvProviderId: agency.hhaxProviderId || agency.evvConfig?.providerId || '',
    evvProviderNpi: agency.evvProviderNpi || '',
    evvProviderApi: agency.evvProviderApi || '',
    evvAdminName: agency.evvSystemAdmin?.name || '',
    evvAdminEmail: agency.evvSystemAdmin?.email || agency.evvConfig?.adminEmail || '',
  };
}

export function validateAgencyForm(formData: AgencyFormState): FormErrors {
  const errors: FormErrors = {};

  if (!formData.legalName.trim()) errors.legalName = 'Agency Legal Name is required';

  if (!formData.npi.trim()) {
    errors.npi = 'NPI is required';
  } else if (!/^\d{10}$/.test(formData.npi)) {
    errors.npi = 'NPI must be a 10-digit number';
  } else if (!isValidNpi(formData.npi)) {
    errors.npi = 'NPI check digit is not valid. Check the number and try again';
  }

  if (!formData.taxId.trim()) {
    errors.taxId = 'Tax ID (EIN) is required';
  } else if (!/^\d{2}-\d{7}$/.test(formData.taxId) && !/^\d{9}$/.test(formData.taxId)) {
    errors.taxId = 'Tax ID should be in format XX-XXXXXXX or XXXXXXXXX';
  }

  if (formData.taxonomyCode.trim() && !TAXONOMY_PATTERN.test(formData.taxonomyCode.trim())) {
    errors.taxonomyCode = 'Taxonomy must be a 10-character NUCC code, e.g. 251E00000X';
  }

  if (formData.benefitCode.length > BENEFIT_CODE_MAX) {
    errors.benefitCode = `Benefit code must be ${BENEFIT_CODE_MAX} characters or fewer`;
  }

  if (!TIMEZONE_VALUES.includes(formData.timezone)) {
    errors.timezone = 'Select a time zone';
  }

  if (!formData.stateLicenseNumber.trim()) errors.stateLicenseNumber = 'State License Number is required';
  if (!formData.officePhone.trim()) errors.officePhone = 'Office Phone is required';

  if (formData.billingEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billingEmail)) {
    errors.billingEmail = 'Please enter a valid email address';
  }

  if (!formData.street.trim()) errors.street = 'Street address is required';
  if (!formData.city.trim()) errors.city = 'City is required';
  if (!formData.state.trim()) {
    errors.state = 'State is required';
  } else if (!/^[A-Za-z]{2}$/.test(formData.state.trim())) {
    errors.state = 'State must be a 2-letter code';
  }

  if (!formData.zipCode.trim()) {
    errors.zipCode = 'ZIP code is required';
  } else if (!ZIP5_PATTERN.test(formData.zipCode)) {
    errors.zipCode = 'ZIP code must be 5 digits';
  }

  if (formData.zipPlus4.trim() && !ZIP4_PATTERN.test(formData.zipPlus4)) {
    errors.zipPlus4 = 'ZIP+4 must be 4 digits';
  }

  Object.assign(errors, validateEvvLinkedIds(formData.evvProviderNpi, formData.evvProviderApi));

  return errors;
}

const STATUS_LABELS: Record<EVVOnboardingStepStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  DONE: 'Done',
};

const STATUS_STYLES: Record<EVVOnboardingStepStatus, string> = {
  NOT_STARTED: 'bg-amber-100 text-amber-700',
  IN_PROGRESS: 'bg-sky-100 text-sky-700',
  DONE: 'bg-green-100 text-green-700',
};

/**
 * The EVV inputs (HHAX provider ID, EVV system admin) open once every step before
 * "Record HHAX provider ID" is done. That step is found by key or label; step 4 otherwise.
 */
function providerIdStepIndex(steps: EvvOnboardingStepDto[]): number {
  // CREDENTIALS_RECEIVED is the legacy key the backend kept for "Record HHAX provider ID"
  const byKey = steps.findIndex((s) => s.key === 'CREDENTIALS_RECEIVED' || /PROVIDER_ID/i.test(s.key) || /provider id/i.test(s.label));
  return byKey >= 0 ? byKey : Math.min(3, steps.length);
}

// ─── Small field helpers ─────────────────────────────────────────────────────

const FieldError: React.FC<{ message?: string; id?: string }> = ({ message, id }) =>
  message ? (
    <div className="flex items-center mt-1" id={id}>
      <AlertCircle size={12} className="text-red-500 mr-1" />
      <p className="text-xs sm:text-sm text-red-500">{message}</p>
    </div>
  ) : null;

interface TextFieldProps {
  name: keyof AgencyFormState;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
  error?: string;
  required?: boolean;
  placeholder?: string;
  type?: string;
  className?: string;
  hint?: string;
  /** Visible helper text under the field */
  note?: string;
  maxLength?: number;
}

const TextField: React.FC<TextFieldProps> = ({
  name, label, value, onChange, disabled, error, required, placeholder, type = 'text', className = 'col-span-1', hint, note, maxLength,
}) => (
  <div className={className}>
    <label htmlFor={`agency-${name}`} className="block text-sm text-left font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <input
        id={`agency-${name}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={note ? `agency-${name}-note` : undefined}
        className={`w-full text-left px-3 py-2 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 ${error ? 'border-red-500' : 'border-gray-300'} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        placeholder={placeholder}
      />
      {hint && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-help" title={hint}>
          <Info size={14} className="sm:w-4 sm:h-4" />
        </div>
      )}
    </div>
    {note && <p id={`agency-${name}-note`} className="mt-1 text-xs text-left text-gray-500">{note}</p>}
    <FieldError message={error} />
  </div>
);

// ─── Component ───────────────────────────────────────────────────────────────

const AgencySetup = () => {
  const { user, refreshPermissions } = useAuth();
  // R-177: PDN is EVV-optional in Texas; the agency decides whether clinicians record it
  const [evvCollectPdn, setEvvCollectPdn] = useState(false);
  const [savingEvvCollectPdn, setSavingEvvCollectPdn] = useState(false);
  // Phase 20A: disciplines the agency offers (referral, scheduling and menus show only these)
  const [servicesOffered, setServicesOffered] = useState<string[]>(() => disciplineCodes());
  const [savingServices, setSavingServices] = useState(false);
  const { showToast } = useToast();

  // Check if user has BOTH create AND update permissions for agencies
  const canEditAgencies = useCanPerformAll(['create:agencies', 'update:agencies']);

  const [formData, setFormData] = useState<AgencyFormState>(EMPTY_FORM);
  const [savedEvv, setSavedEvv] = useState<EvvSaved>(EMPTY_EVV);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [profileMissing, setProfileMissing] = useState<string[] | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentAgencyId, setCurrentAgencyId] = useState<string | null>(null);
  const [evvSteps, setEvvSteps] = useState<EvvOnboardingStepDto[]>([]);
  const [evvLoading, setEvvLoading] = useState(false);
  const [evvError, setEvvError] = useState<string | null>(null);
  const [savingStepKey, setSavingStepKey] = useState<string | null>(null);

  // Ref to track if agency data has been loaded to prevent duplicate calls
  const hasLoadedAgencyRef = useRef(false);

  const applyEvv = useCallback((evv: EvvOnboardingDto) => {
    setEvvSteps(evv.steps);
    const next: EvvSaved = {
      evvProviderId: evv.hhaxProviderId ?? '',
      evvProviderNpi: evv.evvProviderNpi ?? '',
      evvProviderApi: evv.evvProviderApi ?? '',
      evvAdminName: evv.evvSystemAdmin?.name ?? '',
      evvAdminEmail: evv.evvSystemAdmin?.email ?? '',
    };
    setSavedEvv(next);
    // The server value wins, so a cleared provider ID / admin stays cleared
    setFormData((prev) => ({ ...prev, ...next }));
  }, []);

  const loadEvv = useCallback(async (agencyId: string) => {
    setEvvLoading(true);
    setEvvError(null);
    try {
      applyEvv(await getEvvOnboarding(agencyId));
    } catch {
      setEvvError("Couldn't load the EVV checklist.");
    } finally {
      setEvvLoading(false);
    }
  }, [applyEvv]);

  // Load agency data when component mounts
  useEffect(() => {
    if (hasLoadedAgencyRef.current || !user?.tenantId) {
      return;
    }

    const loadAgencyData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let agency: Agency | null = await getAgencyByTenantId(user.tenantId);

        if (!agency) {
          // Create a new basic agency so all later updates have an agency ID
          const basicAgencyData: AgencyData = {
            legalName: '', dba: '', npi: '', taxId: '', medicaidNumber: '', medicareNumber: '',
            stateLicenseNumber: '', officePhone: '', fax: '', billingEmail: '',
            street: '', city: '', state: '', zipCode: '',
          };
          try {
            agency = await createAgency(basicAgencyData);
          } catch {
            // If creation fails, continue with empty form fields
          }
        }

        if (agency) {
          const loaded = agencyToForm(agency);
          setFormData(loaded);
          setSavedEvv({
            evvProviderId: loaded.evvProviderId,
            evvProviderNpi: loaded.evvProviderNpi,
            evvProviderApi: loaded.evvProviderApi,
            evvAdminName: loaded.evvAdminName,
            evvAdminEmail: loaded.evvAdminEmail,
          });
          setEvvCollectPdn(agency.evvCollectPdnVisits === true);
          if (Array.isArray(agency.servicesOffered) && agency.servicesOffered.length) setServicesOffered(agency.servicesOffered);
          if (agency.agencyId) {
            setCurrentAgencyId(agency.agencyId);
            loadEvv(agency.agencyId);
          }
        }
      } catch {
        setError('Failed to load agency data. Please try again later.');
      } finally {
        setIsLoading(false);
        hasLoadedAgencyRef.current = true;
      }
    };

    loadAgencyData();
  }, [user?.tenantId, loadEvv]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const key = name as keyof AgencyFormState;
    let next = value;

    if (key === 'taxId') {
      const digits = value.replace(/[^0-9]/g, '').substring(0, 9);
      next = digits.length > 2 ? `${digits.substring(0, 2)}-${digits.substring(2)}` : digits;
    } else if (key === 'npi' || key === 'evvProviderNpi') {
      next = value.replace(/[^0-9]/g, '').substring(0, 10);
    } else if (key === 'evvProviderApi') {
      next = value.replace(/\s/g, '').substring(0, 15);
    } else if (key === 'zipCode') {
      next = value.replace(/[^0-9]/g, '').substring(0, 5);
    } else if (key === 'zipPlus4') {
      next = value.replace(/[^0-9]/g, '').substring(0, 4);
    } else if (key === 'taxonomyCode') {
      next = value.toUpperCase().replace(/[^0-9A-Z]/g, '').substring(0, 10);
    } else if (key === 'state') {
      next = value.toUpperCase().replace(/[^A-Z]/g, '').substring(0, 2);
    } else if (key === 'benefitCode') {
      next = value.substring(0, BENEFIT_CODE_MAX);
    } else if (key === 'officePhone' || key === 'fax') {
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 3) next = digits;
      else if (digits.length <= 6) next = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      else next = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }

    setFormData((prev) => ({ ...prev, [key]: next }));
  };

  const evvFieldsChanged = formData.evvProviderId !== savedEvv.evvProviderId
    || formData.evvProviderNpi !== savedEvv.evvProviderNpi
    || formData.evvProviderApi !== savedEvv.evvProviderApi
    || formData.evvAdminName !== savedEvv.evvAdminName
    || formData.evvAdminEmail !== savedEvv.evvAdminEmail;

  const handleSubmit = async () => {
    const errors = validateAgencyForm(formData);
    setFormErrors(errors);
    setServerErrors([]);
    setProfileMissing(null);

    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);
    setError(null);

    try {
      const agencyData: AgencyData = {
        legalName: formData.legalName,
        // Optional fields go as '' when blank so the backend clears them (it stores '' as null)
        dba: formData.dba.trim(),
        npi: formData.npi,
        taxId: formData.taxId,
        taxonomyCode: formData.taxonomyCode.trim(),
        benefitCode: formData.benefitCode.trim(),
        timezone: formData.timezone,
        medicaidNumber: formData.medicaidNumber.trim(),
        medicareNumber: formData.medicareNumber.trim(),
        stateLicenseNumber: formData.stateLicenseNumber,
        officePhone: formData.officePhone,
        fax: formData.fax.trim(),
        billingEmail: formData.billingEmail.trim(),
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        zipPlus4: formData.zipPlus4.trim(),
        tenantId: user?.tenantId || undefined,
      };

      let agencyId = currentAgencyId;
      if (agencyId) {
        await updateAgency(agencyId, agencyData);
      } else {
        const newAgency = await createAgency(agencyData);
        agencyId = newAgency.agencyId;
        setCurrentAgencyId(agencyId);
      }

      // EVV provider ID, linked NPI/API and system admin are saved through the evv-onboarding route
      if (agencyId && evvFieldsChanged) {
        const evv = await updateEvvOnboarding(agencyId, {
          hhaxProviderId: formData.evvProviderId.trim(),
          // Blank clears (the backend accepts null)
          evvProviderNpi: formData.evvProviderNpi.trim() || null,
          evvProviderApi: formData.evvProviderApi.trim() || null,
          evvSystemAdmin: {
            name: formData.evvAdminName.trim() || null,
            email: formData.evvAdminEmail.trim() || null,
          },
        });
        applyEvv(evv);
      }

      setIsSubmitted(true);
      window.dispatchEvent(new Event(SETUP_STATUS_UPDATED_EVENT));
    } catch (err) {
      const fieldErrors = getAgencyFieldErrors(err);
      const missing = getAgencyProfileMissing(err);
      if (fieldErrors.length > 0) {
        const mapped: FormErrors = {};
        const unmapped: string[] = [];
        fieldErrors.forEach(({ field, message }) => {
          const leaf = field.split('.').pop() || field;
          const formField = SERVER_FIELD_MAP[leaf];
          if (formField) mapped[formField] = message;
          else unmapped.push(message);
        });
        setFormErrors(mapped);
        setServerErrors(unmapped);
        setError('Some agency details are not valid. Fix the highlighted fields and save again.');
      } else if (missing) {
        setProfileMissing(missing);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save agency information');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleEvvCollectPdnToggle = async (checked: boolean) => {
    if (!currentAgencyId) return;
    setEvvCollectPdn(checked);
    setSavingEvvCollectPdn(true);
    try {
      await updateAgency(currentAgencyId, { evvCollectPdnVisits: checked } as AgencyData);
      await refreshPermissions(); // the visit prompt reads the setting from /users/me/permissions
      showToast(checked ? 'Clinicians will record EVV for PDN visits.' : 'EVV is no longer recorded for PDN visits.', { variant: 'success' });
    } catch {
      setEvvCollectPdn(!checked);
      showToast("Couldn't save the PDN EVV setting. Try again.", { variant: 'error' });
    } finally {
      setSavingEvvCollectPdn(false);
    }
  };

  /** Phase 20A: turn a service on or off; saved at once, at least one stays on. */
  const handleServiceToggle = async (code: string, checked: boolean) => {
    if (!currentAgencyId) return;
    const previous = servicesOffered;
    const next = disciplineCodes().filter(d => (d === code ? checked : previous.includes(d)));
    if (next.length === 0) {
      showToast('Keep at least one service on.', { variant: 'error' });
      return;
    }
    setServicesOffered(next);
    setSavingServices(true);
    try {
      await updateAgency(currentAgencyId, { servicesOffered: next } as AgencyData);
      clearServicesOfferedCache();
      showToast(checked ? `${disciplineLabel(code)} is now offered.` : `${disciplineLabel(code)} is no longer offered for new referrals.`, { variant: 'success' });
    } catch {
      setServicesOffered(previous);
      showToast("Couldn't save the services offered. Try again.", { variant: 'error' });
    } finally {
      setSavingServices(false);
    }
  };

  const handleStepToggle = async (step: EvvOnboardingStepDto, checked: boolean) => {
    if (!currentAgencyId) return;
    const status: EVVOnboardingStepStatus = checked ? 'DONE' : 'NOT_STARTED';
    const previous = evvSteps;
    setEvvSteps((prev) => prev.map((s) => (s.key === step.key ? { ...s, status } : s)));
    setSavingStepKey(step.key);
    try {
      const evv = await updateEvvOnboarding(currentAgencyId, { stepUpdate: { key: step.key, status } });
      if (evv.steps.length > 0) setEvvSteps(evv.steps);
      window.dispatchEvent(new Event('evv-setup-updated'));
    } catch {
      setEvvSteps(previous);
      showToast("Couldn't save the EVV step. Try again.", { variant: 'error' });
    } finally {
      setSavingStepKey(null);
    }
  };

  const gateIndex = providerIdStepIndex(evvSteps);
  const evvInputsUnlocked = evvSteps.length > 0
    && evvSteps.slice(0, gateIndex).every((step) => step.status === 'DONE');
  const evvInputsDisabled = !canEditAgencies || !evvInputsUnlocked;
  const gateStepNumber = gateIndex + 1;

  const fieldProps = (name: keyof AgencyFormState) => ({
    name,
    value: formData[name],
    onChange: handleChange,
    disabled: !canEditAgencies,
    error: formErrors[name],
  });

  const SuccessMessage = () => (
    <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 flex items-center justify-between">
      <div className="flex items-center">
        <div className="bg-green-100 p-1.5 sm:p-2 rounded-full mr-3">
          <Check size={20} className="text-green-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-green-800">Agency Information Saved Successfully!</h2>
          <p className="text-xs sm:text-sm text-green-700">Your agency information has been updated.</p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setIsSubmitted(false)}
        className="text-green-700 text-xs sm:text-sm hover:text-green-900 underline"
      >
        Dismiss
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-2 sm:p-4">
      <FormPageLayout
        onBack={() => {}}
        hideBack={true}
        title="Agency Setup"
        headerRight={<Building size={18} className="text-slate-800" />}
        actions={[{
          key: 'save-agency',
          label: 'Save',
          onClick: handleSubmit,
          variant: 'primary',
          disabled: !canEditAgencies,
          loading: isSaving,
          loadingLabel: 'Saving...',
          icon: <Save size={16} />,
          hidden: !canEditAgencies,
        }]}
        className="min-h-full"
      >
        {isLoading ? (
          <LoadingSpinner className="h-64" />
        ) : (
          <div className="p-4 sm:p-6 text-left">
            {isSubmitted && <SuccessMessage />}
            {error && (
              <Banner variant="error" className="mb-4" data-testid="agency-save-error">
                {error}
                {serverErrors.length > 0 && (
                  <ul className="mt-1 list-disc pl-5">
                    {serverErrors.map((message) => <li key={message}>{message}</li>)}
                  </ul>
                )}
              </Banner>
            )}
            {profileMissing && (
              <Banner variant="warning" title="Agency profile incomplete" className="mb-4" data-testid="agency-profile-missing">
                {profileMissing.length > 0 ? (
                  <>
                    Complete these fields:
                    <ul className="mt-1 list-disc pl-5">
                      {profileMissing.map((label) => <li key={label}>{label}</li>)}
                    </ul>
                  </>
                ) : 'Some required agency details are missing.'}
              </Banner>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 auto-rows-min">
              <TextField {...fieldProps('legalName')} label="Agency Legal Name" required className="col-span-1 sm:col-span-2" placeholder="e.g., Pelora Health, LLC" />
              <TextField {...fieldProps('dba')} label="DBA (Doing Business As)" placeholder="If applicable" />
              <TextField
                {...fieldProps('npi')}
                label="NPI (National Provider Identifier)"
                required
                placeholder="10-digit number"
                hint="Unique 10-digit identification number issued to healthcare providers"
              />
              <TextField {...fieldProps('taxId')} label="Tax ID (EIN)" required placeholder="XX-XXXXXXX" />
              <TextField
                {...fieldProps('taxonomyCode')}
                label="Taxonomy"
                placeholder="e.g., 251E00000X"
                maxLength={10}
                note={PA_FORMS_NOTE}
                hint="10-character NUCC provider taxonomy code (e.g. 251E00000X). Used on PA forms and claims."
              />
              <TextField
                {...fieldProps('benefitCode')}
                label="Benefit code"
                placeholder="TX Medicaid benefit code"
                maxLength={BENEFIT_CODE_MAX}
                note={PA_FORMS_NOTE}
                hint="TX Medicaid benefit code used on PA forms (up to 20 characters)"
              />
              <div className="col-span-1">
                <label htmlFor="agency-timezone" className="block text-sm text-left font-medium text-gray-700 mb-1">
                  Time zone <span className="text-red-500">*</span>
                </label>
                <select
                  id="agency-timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  disabled={!canEditAgencies}
                  className={`w-full px-3 py-2 border rounded-md text-sm bg-white focus:ring-blue-500 focus:border-blue-500 ${formErrors.timezone ? 'border-red-500' : 'border-gray-300'} ${!canEditAgencies ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  {AGENCY_TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
                <FieldError message={formErrors.timezone} />
              </div>
              <TextField {...fieldProps('medicaidNumber')} label="Medicaid Provider Number" placeholder="If billing Medicaid" />
              <TextField {...fieldProps('medicareNumber')} label="Medicare Provider Number" placeholder="If billing Medicare" />
              <TextField {...fieldProps('stateLicenseNumber')} label="State License Number" required placeholder="As issued by your state health department" />
              <TextField {...fieldProps('officePhone')} type="tel" label="Office Phone" required placeholder="(XXX) XXX-XXXX" />
              <TextField {...fieldProps('fax')} type="tel" label="Fax" placeholder="(XXX) XXX-XXXX" />
              <TextField {...fieldProps('billingEmail')} type="email" label="Billing Contact Email" placeholder="billing@youragency.com" />

              {/* Address Section */}
              <div className="col-span-1 sm:col-span-2 mt-3 sm:mt-4">
                <h2 className="text-base sm:text-lg font-medium text-left text-gray-800 mb-2 flex items-center">
                  <MapPin size={16} className="text-slate-800 mr-2" />
                  Agency Address <span className="text-red-500 ml-1">*</span>
                </h2>
                <div className="border-t border-gray-200 pt-3 sm:pt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <TextField {...fieldProps('street')} label="Street Address" required className="col-span-1 sm:col-span-2" placeholder="123 Main St, Suite 100" />
                    <TextField {...fieldProps('city')} label="City" required placeholder="City" />
                    <TextField {...fieldProps('state')} label="State" required placeholder="TX" maxLength={2} />
                    <TextField {...fieldProps('zipCode')} label="ZIP Code" required placeholder="12345" maxLength={5} />
                    <TextField {...fieldProps('zipPlus4')} label="ZIP+4" placeholder="6789" maxLength={4} note={PA_FORMS_NOTE} />
                  </div>
                </div>
              </div>

              {/* Services offered (Phase 20A) */}
              <div id="services" className="col-span-1 sm:col-span-2 mt-4 border-t border-gray-200 pt-4" data-testid="services-offered">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 text-left">Services offered</h3>
                <p className="text-xs text-gray-500 mb-3 text-left">
                  New referrals can be taken only for the services that are on. Turning a service off keeps its existing patients and records.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {disciplineCodes().map(code => (
                    <label key={code} className="flex items-center gap-2 text-left text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={servicesOffered.includes(code)}
                        disabled={!canEditAgencies || !currentAgencyId || savingServices}
                        onChange={(e) => handleServiceToggle(code, e.target.checked)}
                      />
                      {disciplineLongLabel(code)}
                    </label>
                  ))}
                </div>
              </div>

              {/* EVV Setup Section */}
              <div id="evv" className="col-span-1 sm:col-span-2 mt-4 border-t border-gray-200 pt-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 text-left">EVV Setup</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <TextField
                    name="evvProviderId"
                    label="HHAX Provider ID"
                    value={formData.evvProviderId}
                    onChange={handleChange}
                    disabled={evvInputsDisabled}
                    placeholder="Assigned by HHAX"
                  />
                  <TextField
                    name="evvProviderNpi"
                    label="NPI linked to your EVV provider account"
                    value={formData.evvProviderNpi}
                    onChange={handleChange}
                    disabled={evvInputsDisabled}
                    error={formErrors.evvProviderNpi}
                    placeholder="10-digit NPI"
                    note={EVV_LINKED_ID_NOTE}
                  />
                  <TextField
                    name="evvProviderApi"
                    label="API linked to your EVV provider account (if you use an API instead of an NPI)"
                    value={formData.evvProviderApi}
                    onChange={handleChange}
                    disabled={evvInputsDisabled}
                    error={formErrors.evvProviderApi}
                    placeholder="1–15 letters or digits"
                    maxLength={15}
                  />
                  <TextField
                    name="evvAdminName"
                    label="EVV System Admin Name"
                    value={formData.evvAdminName}
                    onChange={handleChange}
                    disabled={evvInputsDisabled}
                    placeholder="Assigned by HHAX"
                  />
                  <TextField
                    name="evvAdminEmail"
                    type="email"
                    label="EVV System Admin Email"
                    value={formData.evvAdminEmail}
                    onChange={handleChange}
                    disabled={evvInputsDisabled}
                    placeholder="Assigned by HHAX"
                  />
                </div>
                {!evvLoading && !savedEvv.evvProviderNpi && !savedEvv.evvProviderApi && (
                  <p className="mt-2 flex items-center gap-1 text-xs text-amber-800" data-testid="evv-linked-id-hint">
                    <Info size={12} aria-hidden="true" /> {EVV_LINKED_ID_HINT}
                  </p>
                )}
                {!evvInputsUnlocked && evvSteps.length > 0 && (
                  <p className="mt-2 text-xs text-gray-500">
                    Complete steps 1–{gateStepNumber - 1} to enter the HHAX provider ID and EVV system admin.
                  </p>
                )}
                <label className="mt-4 flex items-start gap-2 text-left" data-testid="evv-collect-pdn">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={evvCollectPdn}
                    disabled={!canEditAgencies || !currentAgencyId || savingEvvCollectPdn}
                    onChange={(e) => handleEvvCollectPdnToggle(e.target.checked)}
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-800">Record EVV for PDN visits</span>
                    <span className="block text-xs text-gray-500">
                      Texas requires EVV for Medicaid PT, OT, aide and skilled nursing visits. Private duty nursing is optional:
                      turn this on to have nurses confirm HHAX clock-in and clock-out on PDN shifts too. PDN claims are never
                      blocked on EVV.
                    </span>
                  </span>
                </label>
                <div className="mt-4 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-left" data-testid="evv-checklist">
                  {evvLoading && <LoadingSpinner className="h-16" />}
                  {!evvLoading && evvError && (
                    <Banner
                      variant="error"
                      action={currentAgencyId ? (
                        <button
                          type="button"
                          onClick={() => loadEvv(currentAgencyId)}
                          className="rounded-md border border-red-300 bg-white px-3 py-1 text-sm text-red-800 hover:bg-red-100"
                        >
                          Retry
                        </button>
                      ) : undefined}
                    >
                      {evvError}
                    </Banner>
                  )}
                  {!evvLoading && !evvError && evvSteps.map((step, index) => {
                    const isDone = step.status === 'DONE';
                    return (
                      <div key={step.key} className="flex flex-wrap items-start gap-3 text-sm text-gray-700" data-testid="evv-step">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={isDone}
                          onChange={(event) => handleStepToggle(step, event.target.checked)}
                          disabled={!canEditAgencies || savingStepKey !== null}
                          aria-label={`Mark step ${index + 1} complete`}
                        />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium">Step {index + 1}: {step.label}</span>
                          {step.description && <p className="text-xs text-gray-500">{step.description}</p>}
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[step.status]}`}>
                          {STATUS_LABELS[step.status]}
                        </span>
                        {step.resourceUrl && (
                          <a
                            href={step.resourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-700 underline whitespace-nowrap"
                          >
                            Resource <ExternalLink size={12} aria-hidden="true" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </FormPageLayout>
    </div>
  );
};

export default AgencySetup;
